import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import type { GetStaticProps } from "next";
import Head from "next/head";
import { Navbar } from "components/modules/Navigation/Navbar/Navbar";
import { Tools } from "components/modules/Layout/Tools/Tools";
import { Footer } from "components/modules/Layout/Footer/Footer";
import { commonLanguageFiles } from "client/config";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { ReactNode } from "react";
import Link from "@/components/common/Link";
import { InternationalizationProps } from "components/modules/Internationalization/Internationalization";
import { RouteNames } from "@/RouteNames";

interface AboutProps {
    href: InternationalizationProps["url"];
    text: string;
}

const Anchor = ({ href, text }: AboutProps) => (
    <Link href={href?.toString()} className="text-irBlue-600 dark:text-irBlue-200">
        {text}
    </Link>
);

const ChangeLogPage = () => {
    return (
        <>
            <Head>
                <title>{`Change Logs | ImageResizer.com`}</title>
            </Head>
            <div className="text-black max-w-1440 page-shadow dark:text-white dark:bg-darkSurface-100">
                <Navbar url="/changelogs" />
                <div className="flex flex-col gap-8 py-8 mx-8 leading-relaxed md:mx-24 lg:mx-44">
                    <h1 className="text-3xl font-semibold capitalize">Change Logs</h1>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.7.12">v3.7.12</Title>
                        <div>10th April, 2025</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">
                            <span className="" dir="">
                                <p>
                                    <strong>Format-Specific Croppers Are Live!</strong>
                                </p>
                                <p>
                                    We’re excited to roll out our brand-new <strong>format-specific croppers</strong>,
                                    designed to bring more precision and efficiency to your image editing workflow!
                                </p>
                                <div>
                                    With this release, we’re introducing dedicated cropping tools for the most popular
                                    image formats:
                                    <ul className="list-disc ml-4">
                                        <li>
                                            <Anchor href="/crop-jpg" text="JPG/JPEG Cropper" /> – Crop JPG or JPEG
                                            images with sharp accuracy.
                                        </li>
                                        <li>
                                            <Anchor href="/crop-png" text="PNG Cropper" /> – Trim PNGs while preserving
                                            their transparent backgrounds.{" "}
                                        </li>
                                        <li>
                                            <Anchor href="/crop-webp" text="WebP Cropper" /> – Perfectly crop WebP
                                            images for web and digital use.{" "}
                                        </li>
                                    </ul>
                                    These new format-specific resizers provide users with a more tailored and efficient
                                    resizing experience, ensuring optimal results for each image type.
                                </div>
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.7.9">v3.7.9</Title>
                        <div>3rd February, 2025</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">
                            <span className="" dir="">
                                <p>
                                    <strong>
                                        We are excited to announce the launch of our image format-specific resizers!
                                    </strong>
                                </p>
                                <div>
                                    With this release, we are introducing dedicated tools for resizing specific image
                                    formats:
                                    <ul className="list-disc ml-4">
                                        <li>
                                            <Anchor href="/resize-jpg" text="JPG/JPEG Resizer" /> – Resize JPG/JPEG
                                            images with precision while maintaining quality.
                                        </li>
                                        <li>
                                            <Anchor href="/resize-png" text="PNG Resizer" /> – Resize PNG images
                                            effortlessly.
                                        </li>
                                        <li>
                                            <Anchor href="/resize-webp" text="WebP Resizer" /> – Optimize WebP images
                                            for web and digital use.
                                        </li>
                                    </ul>
                                    These new format-specific resizers provide users with a more tailored and efficient
                                    resizing experience, ensuring optimal results for each image type.
                                </div>
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.7.7">v3.7.7</Title>
                        <div>17th January, 2025</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">
                            <span className="" dir="">
                                <p>
                                    <strong>Merge Option for Editor Page</strong>
                                </p>
                                <div>
                                    <ul className="list-disc ml-4">
                                        <li>
                                            Added the ability to merge multiple images into a single PDF or GIF with
                                            various customizable settings.
                                            <ul className="list-disc ml-4">
                                                <li>
                                                    Supports merging of images into <strong>PDF</strong> and{" "}
                                                    <strong>GIF</strong> formats.
                                                </li>
                                                <li>
                                                    New sorting functionality: users can now drag and drop images to
                                                    reorder them before merging.
                                                </li>
                                            </ul>
                                        </li>
                                        <li>Some Bug Fixes.</li>
                                    </ul>
                                </div>
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.7.4">v3.7.4</Title>
                        <div>2nd January, 2025</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">
                            <span className="" dir="">
                                <p>
                                    <strong>New Language Support:</strong>
                                </p>
                                <div>
                                    <ul className="list-disc ml-4">
                                        <li>Dutch</li>
                                        <li>日本語 (Japanese)</li>
                                        <li>简体中文 (Simplified Chinese)</li>
                                        <li>한국어 (Korean)</li>
                                        <li>Svenska (Swedish)</li>
                                        <li>Türkçe (Turkish)</li>
                                        <li>Bahasa Indonesia</li>
                                        <li>Polish</li>
                                    </ul>
                                </div>
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.7.1">v3.7.1</Title>
                        <div>26th December, 2024</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">
                            <span className="" dir="">
                                <p>
                                    This release introduces new image converters which support variety of image formats.
                                    Such as, <Anchor href="/png-converter" text="PNG Converter" />,{" "}
                                    <Anchor href="/svg-converter" text="SVG Converter" />,{" "}
                                    <Anchor href="/gif-converter" text="GIF Converter" /> and many more.
                                </p>
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.7.0">v3.7.0</Title>
                        <div>22nd December, 2024</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">
                            <span className="" dir="">
                                <p>
                                    This release introduces our new{" "}
                                    <Anchor href="/image-converter" text="Image Converter" /> tool.
                                </p>
                                <p>
                                    <strong>New Features:</strong>
                                </p>
                                <div>
                                    <ul className="list-disc ml-4">
                                        <li>Added new Image Converter tool to and from a variety of image formats.</li>
                                        <li>Supports bulk image processing.</li>
                                        <li>Includes advanced settings for enhanced customization and control.</li>
                                        <li>Improved user interface for easier navigation and usage.</li>
                                    </ul>
                                </div>
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.6.9">v3.6.9</Title>
                        <div>4th December, 2024</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">Some critical bug fixes.</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.6.2">v3.6.2</Title>
                        <div>8th October, 2024</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">
                            <span className="" dir="">
                                <p>
                                    <strong>New Features:</strong>
                                </p>
                                <div>
                                    <ul className="list-disc ml-4">
                                        <li>
                                            <strong>
                                                <Anchor href={RouteNames.gifCompressor} text="GIF Compressor:" />
                                            </strong>
                                            &nbsp;A separate page dedicated to compressing Gif images improves quality
                                            and processing speed.
                                        </li>
                                    </ul>
                                </div>
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.6.0">v3.6.0</Title>
                        <div>2nd October, 2024</div>
                        <meta charSet="utf-8" />
                        <span data-teams="true">
                            <span className="" dir="">
                                <p>
                                    This release introduces new features designed to enhance the quality of our
                                    services.
                                </p>
                                <p>
                                    <strong>New Features:</strong>
                                </p>
                                <div>
                                    <ul className="list-disc ml-4">
                                        <li>
                                            <strong>
                                                <Anchor
                                                    href={RouteNames.jpegCompressor}
                                                    text={"JPEG/JPG compressor:"}
                                                />
                                            </strong>
                                            &nbsp;A new dedicated page for compressing JPEG and JPG images enhances
                                            efficiency.
                                        </li>
                                        <li>
                                            <strong>
                                                <Anchor href={RouteNames.pngCompressor} text="PNG Compressor:" />
                                            </strong>
                                            &nbsp;A separate page dedicated to compressing PNG images improves quality
                                            and processing speed.
                                        </li>
                                        <li>
                                            <strong>Customer Support Desk:</strong>&nbsp;Enhanced support for quicker
                                            and more effective customer assistance.
                                        </li>
                                    </ul>
                                </div>
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.15">v3.5.15</Title>
                        <div>13th May, 2024</div>
                        <div>Important bug fix</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.14">v3.5.14</Title>
                        <div>12th December, 2023</div>
                        <div>Important Bug fixes</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.13">v3.5.13</Title>
                        <div>4th December, 2023</div>
                        <div>Small Updates</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.12">v3.5.12</Title>
                        <div>15th November, 2023</div>
                        <div>Some Bug fixes.</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.11">v3.5.11</Title>
                        <div>26th September, 2023</div>
                        <div>
                            This release is centered around bug fixes and a new feature in our{" "}
                            <Anchor href={"/bulk-resize"} text="Bulk Resize Tool" />. Now every export will have it's
                            unique URL (available upto 4 hours) and users can share the URL or open it on a different
                            browser.
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.10">v3.5.10</Title>
                        <div>17th September, 2023</div>
                        <div>Some Bug Fixes</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.9">v3.5.9</Title>
                        <div>16th September, 2023</div>
                        <div>Some Bug Fixes</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.8">v3.5.8</Title>
                        <div>11th September, 2023</div>
                        <div>
                            <ul className="ml-4 list-disc">
                                <li>
                                    New sort features in our <Anchor href="/bulk-resize" text="Bulk Resizer" /> and{" "}
                                    <Anchor href="/image-compressor" text="Image Compressor" /> tools.
                                </li>
                                <li>
                                    Dragging textboxes in our <Anchor href="/meme-generator" text="Meme Generator" /> is
                                    now more user friendly.
                                </li>
                                <li>Some more SEO fixes.</li>
                            </ul>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.7">v3.5.7</Title>
                        <div>4th September, 2023</div>
                        <div>
                            Bug fixes for our <Anchor href="/meme-generator" text="Meme Generator" /> tool.
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.6">v3.5.6</Title>
                        <div>2nd September, 2023</div>
                        <div>SEO Fix</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.5">v3.5.5</Title>
                        <div>29th August, 2023</div>
                        <div>Security Patch</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.4">v3.5.4</Title>
                        <div>28th August, 2023</div>
                        <div>
                            This release includes performance improvements, bug fixes and new features. These are listed
                            below.
                            <ul className="ml-4 list-disc">
                                <li>
                                    We have added the internationalized urls to our sitemap for crawlers to better
                                    recognize the pages.
                                </li>
                                <li>
                                    Some bug fixes in our <Anchor href="/" text="Single Image Resizer" /> and{" "}
                                    <Anchor href="/color-picker" text="Color Picker" /> tools.
                                </li>
                                <li>
                                    Now every meme in our <Anchor href="/meme-generator" text="Meme Generator" /> tool
                                    will have it's own editor url. This allows users to come back to the same meme
                                    easily and also to share a meme from our site!
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.3">v3.5.3</Title>
                        <div>12th August, 2023</div>
                        <div>
                            UI improvements for our <Anchor href="/image-compressor" text="Image Compressor" /> and{" "}
                            <Anchor href="/" text="Single Image Resizer" /> tools.
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.2">v3.5.2</Title>
                        <div>8th August, 2023</div>
                        <div>We have deployed some internal changes. The UI is unchanged.</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.1">v3.5.1</Title>
                        <div>3rd August, 2023</div>
                        <div>
                            This release is focused around our <Anchor href="/color-picker" text="Color Picker" /> tool
                            . Here is a list of new features we introduced:
                            <ul className="ml-4 list-disc">
                                <li>
                                    We have built a new "Color Contrast Picker" tool which which users can easily
                                    evaluate and adjust "Text Color" and "Background Color" contrast for optimal
                                    readability and design brilliance. Elevate your content and create stunning visuals
                                    effortlessly
                                </li>
                                <li>Some UI improvements in the color picker tool</li>
                                <li>
                                    Some UI changes in our <Anchor href="/image-compressor" text="Image Compressor" />{" "}
                                    tool so that users can more easily change compression settings in the editor and
                                    apply those changes to all the available images.
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Title id="v.3.5.0">v3.5.0</Title>
                        <div>1st August, 2023</div>
                        <div>
                            This release is focused around our <Anchor href="/color-picker" text="Color Picker" /> tool
                            . Here is a list of new features we introduced:
                            <ul className="ml-4 list-disc">
                                <li>
                                    Users can now edit the selected color either by typing or by adjusting the range
                                    sliders in RGB, HSL, HSV, CMYK or HEX format.
                                </li>
                                <li>
                                    Color Harmonies section has been given it's own section below the color picker. Now
                                    users can interact with this section with more ease.
                                </li>
                                <li>
                                    Users can now see and copy Tints and Shades of their selected color in a separate
                                    section.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <Tools />
                <Footer url="/changelogs" />
            </div>
        </>
    );
};

const Title = ({ id, children }: { id: string; children: ReactNode }) => (
    <div className="text-xl font-semibold" id={id}>
        {children}
    </div>
);

const files = [...commonLanguageFiles];

const LanguageWrapper = () => (
    <LanguageProvider files={files}>
        <ChangeLogPage />
    </LanguageProvider>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
